"use client";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  LayoutDashboard,
  FileText,
  Briefcase,
  Globe,
  ArrowRight,
  ArrowLeft,
} from "lucide-react";

interface TourStepProps {
  onNext: () => void;
  onBack?: () => void;
  isFirst?: boolean;
  isLast?: boolean;
}

const tourItems = [
  {
    icon: LayoutDashboard,
    title: "Dashboard Overview",
    description: "Your central hub for managing everything",
    features: [
      "Quick stats and metrics at a glance",
      "Recent activity and upcoming tasks",
      "Revenue tracking and trends",
      "Customizable quick action buttons",
    ],
    gradient: "from-blue-500 to-cyan-500",
  },
  {
    icon: FileText,
    title: "Creating Quotes",
    description: "Professional estimates in minutes",
    features: [
      "Choose from pre-built templates",
      "Add line items from your Price Book",
      "Include photos and detailed descriptions",
      "Send quotes instantly via email or SMS",
    ],
    gradient: "from-purple-500 to-pink-500",
  },
  {
    icon: Briefcase,
    title: "Managing Jobs",
    description: "Track work from start to finish",
    features: [
      "Convert quotes to jobs with one click",
      "Assign team members and schedule work",
      "Track time and materials in real-time",
      "Update job status and keep clients informed",
    ],
    gradient: "from-green-500 to-emerald-500",
  },
  {
    icon: Globe,
    title: "Client Portal",
    description: "Let clients serve themselves 24/7",
    features: [
      "Clients can view and approve quotes online",
      "Online booking for new appointments",
      "Access to job history and invoices",
      "Two-way messaging and notifications",
    ],
    gradient: "from-orange-500 to-red-500",
  },
];

export function TourStep({ onNext, onBack }: TourStepProps) {
  return (
    <div className="space-y-8 py-6 max-w-5xl mx-auto">
      <div className="text-center space-y-2">
        <h2 className="text-3xl font-bold tracking-tight">
          Quick Tour of Key Features
        </h2>
        <p className="text-muted-foreground text-lg">
          Here's how JobWright streamlines your daily workflow
        </p>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {tourItems.map((item, index) => (
          <Card key={item.title} className="overflow-hidden">
            <CardHeader className="pb-3">
              <div className="flex items-start gap-4">
                <div
                  className={`flex h-14 w-14 shrink-0 items-center justify-center rounded-xl bg-gradient-to-br ${item.gradient} shadow-lg`}
                >
                  <item.icon className="h-7 w-7 text-white" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="flex h-6 w-6 items-center justify-center rounded-full bg-muted text-xs font-bold">
                      {index + 1}
                    </span>
                    <CardTitle className="text-lg">{item.title}</CardTitle>
                  </div>
                  <CardDescription>{item.description}</CardDescription>
                </div>
              </div>
            </CardHeader>
            <CardContent>
              <ul className="space-y-2">
                {item.features.map((feature) => (
                  <li key={feature} className="flex items-start gap-2 text-sm">
                    <span className="text-success dark:text-green-400 mt-0.5">✓</span>
                    <span className="text-muted-foreground">{feature}</span>
                  </li>
                ))}
              </ul>
            </CardContent>
          </Card>
        ))}
      </div>

      <div className="grid gap-4 md:grid-cols-3">
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-950 dark:to-indigo-950 border-info dark:border-blue-800">
          <CardContent className="pt-6 text-center space-y-2">
            <div className="text-3xl font-bold text-info dark:text-info">5 min</div>
            <p className="text-sm text-muted-foreground">
              Average time to create a professional quote
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-950 dark:to-pink-950 border-purple-200 dark:border-purple-800">
          <CardContent className="pt-6 text-center space-y-2">
            <div className="text-3xl font-bold text-info dark:text-purple-400">24/7</div>
            <p className="text-sm text-muted-foreground">
              Client portal access for bookings and quotes
            </p>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-950 dark:to-emerald-950 border-success dark:border-green-800">
          <CardContent className="pt-6 text-center space-y-2">
            <div className="text-3xl font-bold text-success dark:text-green-400">∞</div>
            <p className="text-sm text-muted-foreground">
              Unlimited jobs, quotes, and team members
            </p>
          </CardContent>
        </Card>
      </div>

      <div className="flex justify-between pt-4">
        <Button
          variant="outline"
          onClick={onBack}
          className="gap-2"
        >
          <ArrowLeft className="h-4 w-4" />
          Back
        </Button>
        <Button
          onClick={onNext}
          className="gap-2"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}
